import axios from "axios";
import { getErrorMessage, handleApiError } from "../utils/errorHandler";
import API_BASE_URL from "../config/api";

// Base URL for your Flask backend
const BASE_URL = API_BASE_URL;
console.log('🔧 Coarse Aggregate API - Base URL:', BASE_URL);

// Create a new Coarse Aggregate test request
export const createCoarseAggregateTestRequest = async (testRequestData) => {
  const url = `${BASE_URL}/coarse-aggregate/test-requests`;
  console.log('🌐 API Call - Creating Coarse Aggregate test request');
  console.log('📍 URL:', url);
  console.log('📦 Data:', testRequestData);
  console.log('🔧 Base URL:', BASE_URL);
  console.log('🌍 Environment:', process.env.NODE_ENV);
  
  try {
    const response = await axios.post(url, testRequestData);
    console.log('✅ Success:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating Coarse Aggregate test request:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: url,
      baseUrl: BASE_URL
    });
    
    // Check if it's a 404 error
    if (error.response?.status === 404) {
      const errorMsg = `API endpoint not found (404). The backend route may not be deployed.\n\n` +
        `Expected endpoint: POST ${url}\n\n` +
        `Please ensure:\n` +
        `1. The backend code with 'register_coarse_aggregate_routes' is deployed\n` +
        `2. The Flask server has been restarted after deployment\n` +
        `3. Check server logs to verify routes are registered\n` +
        `4. Verify the route exists in backend/coarse_aggregate_api.py`;
      
      console.error("🚨 DEPLOYMENT ISSUE DETECTED:");
      console.error(errorMsg);
      
      // Show user-friendly alert with deployment guidance
      alert(`⚠️ Backend Endpoint Not Found\n\n` +
        `The Coarse Aggregate API endpoint is not available on the server.\n\n` +
        `This usually means:\n` +
        `• The backend code needs to be deployed\n` +
        `• The server needs to be restarted\n\n` +
        `Please contact your system administrator.\n\n` +
        `Endpoint: ${url}`);
      
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      enhancedError.isDeploymentError = true;
      throw enhancedError;
    }
    
    // Extract user-friendly error message for other errors
    const errorMessage = getErrorMessage(error);
    
    // Show user-friendly alert
    handleApiError(error, "Failed to create Coarse Aggregate test request. Please try again.");
    
    // Re-throw with user-friendly message
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Save Coarse Aggregate test observations
export const saveCoarseAggregateObservations = async (testRequestId, observationsData) => {
  const url = `${BASE_URL}/coarse-aggregate/test-observations/${testRequestId}`;
  console.log('🌐 API Call - Saving Coarse Aggregate observations');
  console.log('📍 URL:', url);
  console.log('📦 Test Request ID:', testRequestId);
  
  try {
    const response = await axios.post(url, observationsData);
    console.log('✅ Success:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error saving Coarse Aggregate observations:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: url
    });
    handleApiError(error, "Failed to save Coarse Aggregate observations. Please try again.");
    throw error;
  }
};

// Get Coarse Aggregate test observations
export const getCoarseAggregateObservations = async (testRequestId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/coarse-aggregate/test-observations/${testRequestId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching Coarse Aggregate observations:", error);
    handleApiError(error, "Failed to fetch Coarse Aggregate observations. Please try again.");
    throw error;
  }
};

// Get all coarse aggregate test requests (for dashboard)
export const getAllCoarseAggregateTestRequests = async () => {
  const url = `${BASE_URL}/coarse-aggregate/test-requests`;
  try {
    console.log('🔵 [COARSE AGGREGATE API] Fetching all test requests for dashboard');
    console.log('🌐 [COARSE AGGREGATE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [COARSE AGGREGATE API] All test requests fetched successfully:', response.data);
    return response.data.test_requests || [];
  } catch (error) {
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [COARSE AGGREGATE API] Error fetching all test requests");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    if (errorStatus === 404) {
      console.warn("⚠️ [COARSE AGGREGATE API] Endpoint not found (404). The backend route may not be deployed.");
      console.warn("   Expected endpoint: GET /api/coarse-aggregate/test-requests");
      console.warn("   Please ensure the backend is deployed with the latest code from backend/app.py");
    }
    
    // Return empty array on error to not break dashboard
    return [];
  }
};

// Get a single coarse aggregate test request by ID
export const getCoarseAggregateTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/coarse-aggregate/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [COARSE AGGREGATE API] Fetching test request ID:', testRequestId);
    console.log('🌐 [COARSE AGGREGATE API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [COARSE AGGREGATE API] Test request fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [COARSE AGGREGATE API] Error fetching test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    handleApiError(error, "Failed to fetch Coarse Aggregate test request. Please try again.");
    throw error;
  }
};

// Update a coarse aggregate test request
export const updateCoarseAggregateTestRequest = async (testRequestId, testRequestData) => {
  const url = `${BASE_URL}/coarse-aggregate/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [COARSE AGGREGATE API] Updating test request ID:', testRequestId);
    console.log('🌐 [COARSE AGGREGATE API] Full URL:', url);
    console.log('📦 Data:', testRequestData);
    const response = await axios.put(url, testRequestData);
    console.log('✅ [COARSE AGGREGATE API] Test request updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [COARSE AGGREGATE API] Error updating test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    const errorMessage = getErrorMessage(error);
    handleApiError(error, "Failed to update Coarse Aggregate test request. Please try again.");
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Delete a coarse aggregate test request
export const deleteCoarseAggregateTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/coarse-aggregate/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [COARSE AGGREGATE API] Deleting test request ID:', testRequestId);
    console.log('🌐 [COARSE AGGREGATE API] Full URL:', url);
    const response = await axios.delete(url);
    console.log('✅ [COARSE AGGREGATE API] Test request deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [COARSE AGGREGATE API] Error deleting test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    handleApiError(error, "Failed to delete Coarse Aggregate test request. Please try again.");
    throw error;
  }
};

